import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { initGA, trackPageView } from "./utils/analytics";

// Components
import Header from "./components/Header";
import Hero from "./components/Hero";
import AIAnalyzer from "./components/AIAnalyzer";
import GEDSolutions from "./components/GEDSolutions";
import Integrations from "./components/Integrations";
import News from "./components/News";
import About from "./components/About";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import FAQModal from "./components/FAQModal";
import LegalModals from "./components/LegalModals";
import LeadMagnet from "./components/LeadMagnet";
import Sponsors from "./components/Sponsors";
import SolutionLanding from "./components/SolutionLanding";
import AdminDashboard from "./components/AdminDashboard";
import ReturnVisitorPopup from "./components/ReturnVisitorPopup";
import EBook from "./components/EBook";
import SponsorsPage from "./components/SponsorsPage";
// GDNLanding removed — /solutions/gdn-solutions now returns 410 Gone
import BlogArticle from "./components/BlogArticle";
import ObligationsLanding from "./components/ObligationsLanding";
import Gone from "./components/Gone";
import { Navigate } from "react-router-dom";

// Data
import { translations } from "./data/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Language Context
export const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

// Detect initial language — URL param has HIGHEST PRIORITY (crawler-friendly)
const detectLanguage = () => {
  // 1. URL ?lang= parameter — ALWAYS wins. Ensures Googlebot & crawlers get the
  //    correct localized content on the very first render (no useEffect delay).
  try {
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang && ["fr", "es", "en"].includes(urlLang.toLowerCase())) {
      const normalized = urlLang.toLowerCase();
      // Persist the URL choice so subsequent same-tab navigations remain consistent
      localStorage.setItem("documatch_language", normalized);
      return normalized;
    }
  } catch (e) { /* SSR-safe fallback */ }

  // 2. localStorage manual override (only if no URL param)
  const saved = localStorage.getItem("documatch_language");
  if (saved && ["fr", "es", "en"].includes(saved)) return saved;

  // 3. Detect from browser language
  const browserLang = (navigator.language || navigator.userLanguage || "").toLowerCase();
  if (browserLang.startsWith("es")) return "es";
  if (browserLang.startsWith("fr")) return "fr";
  if (browserLang.startsWith("en")) return "en";

  // 4. Default to FR
  return "fr";
};

function App() {
  const [language, setLanguage] = useState(detectLanguage);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showAnalyzer, setShowAnalyzer] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  const [cookieAccepted, setCookieAccepted] = useState(() => {
    return localStorage.getItem("cookieAccepted") === "true";
  });

  const t = translations[language];

  // Persist language choice
  const handleSetLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("documatch_language", lang);
  };

  // React to URL ?lang= changes at runtime (popstate/pushstate) so crawlers
  // and users navigating between language variants get instant updates.
  useEffect(() => {
    const applyUrlLang = () => {
      try {
        const urlLang = new URLSearchParams(window.location.search).get("lang");
        if (urlLang && ["fr", "es", "en"].includes(urlLang.toLowerCase())) {
          const normalized = urlLang.toLowerCase();
          if (normalized !== language) {
            setLanguage(normalized);
            localStorage.setItem("documatch_language", normalized);
          }
        }
      } catch (e) { /* ignore */ }
    };
    window.addEventListener("popstate", applyUrlLang);
    return () => window.removeEventListener("popstate", applyUrlLang);
  }, [language]);

  // SEO: Update document title, meta, canonical + hreflang based on language
  useEffect(() => {
    document.title = t.seo.title;
    
    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", t.seo.description);

    // Meta keywords
    let metaKw = document.querySelector('meta[name="keywords"]');
    if (!metaKw) {
      metaKw = document.createElement("meta");
      metaKw.name = "keywords";
      document.head.appendChild(metaKw);
    }
    metaKw.setAttribute("content", t.seo.keywords);

    // HTML lang attribute
    document.documentElement.lang = language;

    // --- SEO: Self-referencing CANONICAL + hreflang (fix Search Console warning) ---
    // Canonical must depend on URL (not on resolved language state), so bare `/`
    // always self-canonicalizes to `/` regardless of visitor's browser locale.
    const origin = "https://www.documatch.eu";
    const path = window.location.pathname;
    let urlLang = null;
    try {
      const q = new URLSearchParams(window.location.search).get("lang");
      if (q && ["fr", "es", "en"].includes(q.toLowerCase())) urlLang = q.toLowerCase();
    } catch (e) { /* ignore */ }
    const canonicalUrl = `${origin}${path}${urlLang ? `?lang=${urlLang}` : ""}`;

    // Replace canonical
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.rel = "canonical";
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.href = canonicalUrl;

    // Rebuild hreflang tags cleanly
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    const alternates = [
      { hl: "fr", href: `${origin}${path}` },
      { hl: "es", href: `${origin}${path}?lang=es` },
      { hl: "en", href: `${origin}${path}?lang=en` },
      { hl: "x-default", href: `${origin}${path}` }
    ];
    alternates.forEach(({ hl, href }) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = hl;
      link.href = href;
      document.head.appendChild(link);
    });
  }, [language, t]);

  // Track visitor on first load + init GA
  useEffect(() => {
    initGA();
    const hasVisited = sessionStorage.getItem("visited");
    if (!hasVisited) {
      axios.post(`${API}/track-visit`).catch(() => {});
      sessionStorage.setItem("visited", "true");
    }
  }, []);

  const handleCookieAccept = () => {
    localStorage.setItem("cookieAccepted", "true");
    setCookieAccepted(true);
  };

  const handleCookieReject = () => {
    localStorage.setItem("cookieAccepted", "false");
    setCookieAccepted(true);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const HomePage = () => {
    useEffect(() => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        if (hash === "analyzer") setShowAnalyzer(true);
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }, []);

    return (
    <>
      <Header 
        onNavigate={scrollToSection} 
        onShowAnalyzer={() => {
          setShowAnalyzer(true);
          scrollToSection("analyzer");
        }}
      />
      
      <main>
        <Hero 
          onStartAnalysis={() => {
            setShowAnalyzer(true);
            setTimeout(() => scrollToSection("analyzer"), 100);
          }}
          onShowFAQ={() => setShowFAQ(true)}
        />

        {/* Documatch CONTEXT premium card — floating between Hero and next section with breathing space */}
        <section className="relative px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 pb-10 sm:pb-14 bg-white" data-testid="context-card-section">
          <div className="max-w-5xl mx-auto animate-fade-in-up">
            <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-700 p-6 sm:p-10 lg:p-12 shadow-2xl shadow-blue-950/40 relative overflow-hidden border border-white/10">
              {/* decorative glows */}
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-purple-400/15 rounded-full blur-3xl pointer-events-none" />

              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-6 lg:gap-10">
                <div className="flex-1 text-center lg:text-left">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white/90 text-[11px] font-semibold uppercase tracking-[0.14em] mb-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
                    Documatch · CONTEXT
                  </span>
                  <h2 className="font-playfair text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mb-3" data-testid="context-card-title">
                    {language === "fr" ? "Avant de comparer des logiciels, comprenez votre contexte documentaire."
                      : language === "es" ? "Antes de comparar software, entiende tu contexto documental."
                      : "Before comparing software, understand your document context."}
                  </h2>
                  <p className="text-white/85 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto lg:mx-0">
                    {language === "fr" ? "Documatch CONTEXT analyse vos processus, vos contraintes et vos objectifs afin de préparer une recherche GED réellement adaptée à votre organisation."
                      : language === "es" ? "Documatch CONTEXT analiza tus procesos, restricciones y objetivos para preparar una búsqueda GED realmente adaptada a tu organización, tu ERP y tu sector."
                      : "Documatch CONTEXT maps your processes, constraints and objectives to prepare a DMS shortlist that is genuinely fit for your organisation, your ERP stack and your European compliance context."}
                  </p>
                </div>
                <div className="flex-shrink-0 w-full lg:w-auto">
                  <a
                    href="https://context.documatch.eu/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full lg:w-auto inline-flex items-center justify-center gap-2 px-7 py-4 bg-white text-blue-700 hover:bg-blue-50 font-bold rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-[1.02] min-h-[48px] text-sm sm:text-base"
                    data-testid="cta-context-documatch"
                  >
                    {language === "fr" ? "Découvrir CONTEXT" : language === "es" ? "Descubrir CONTEXT" : "Discover CONTEXT"}
                    <span className="text-lg font-bold">→</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 bg-white">
          <Sponsors />
        </section>

        {/* Bridge section: 2026 Document Obligations landing teaser */}
        <section className="py-12 px-4 bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-y border-blue-100" data-testid="obligations-bridge">
          <div className="max-w-5xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-wider mb-4">
              {language === "fr" ? "Réglementation 2026" : language === "es" ? "Reglamentación 2026" : "2026 Regulation"}
            </span>
            <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              {language === "fr" ? "Facturation électronique, RGPD, archivage légal : êtes-vous prêt pour 2026 ?"
                : language === "es" ? "Facturación electrónica, RGPD, archivado legal: ¿estás listo para 2026?"
                : "E-invoicing, GDPR, legal archiving: are you ready for 2026?"}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mb-6 text-sm sm:text-base">
              {language === "fr" ? "Guide complet sur les obligations documentaires 2026 — facturation électronique, RGPD, archivage à valeur probante, dématérialisation et transformation digitale."
                : language === "es" ? "Guía completa sobre las obligaciones documentales 2026 — facturación electrónica, RGPD, archivado con valor probatorio, desmaterialización y transformación digital."
                : "Complete guide to 2026 document obligations — e-invoicing, GDPR, legal-grade archiving, paperless and digital transformation."}
            </p>
            <a href="/obligations-2026" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors shadow-md hover:shadow-lg" data-testid="cta-obligations-2026">
              {language === "fr" ? "Découvrir le guide 2026 →" : language === "es" ? "Descubrir la guía 2026 →" : "Discover the 2026 guide →"}
            </a>
          </div>
        </section>

        {/* External CTA — lab.documatch.eu (Documentary maturity assessment, adapted per market) */}
        <section className="py-12 px-4 bg-slate-50" data-testid="lab-cta">
          <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 p-8 sm:p-12 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none" />
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-xs font-semibold uppercase tracking-wider mb-4">
                {language === "fr" ? "Évaluation · 20 questions expertes"
                  : language === "es" ? "Evaluación · 20 preguntas expertas"
                  : "Assessment · 20 expert questions"}
              </span>
              <h2 className="font-playfair text-2xl sm:text-3xl font-bold mb-3 max-w-3xl mx-auto">
                {language === "fr" ? "Votre entreprise est-elle prête pour les nouvelles obligations documentaires de 2026 ?"
                  : language === "es" ? "¿Está tu empresa preparada para las nuevas obligaciones documentales de 2026?"
                  : "Is your organisation ready for the 2026 European document compliance shift?"}
              </h2>
              <p className="text-white/85 text-sm sm:text-base max-w-2xl mx-auto mb-6">
                {language === "fr" ? "20 questions expertes pour évaluer la maturité documentaire de votre organisation face à la facturation électronique, à l'archivage légal et aux nouvelles exigences réglementaires françaises."
                  : language === "es" ? "20 preguntas expertas para evaluar la madurez documental de tu organización frente a la factura electrónica B2B (Ley Crea y Crece), VeriFactu, conservación documental y las nuevas exigencias regulatorias españolas."
                  : "20 expert questions to benchmark your document maturity against European B2B priorities: PEPPOL eInvoicing, GDPR, records management, audit readiness and information governance across Germany, the Netherlands, Belgium and Luxembourg."}
              </p>
              <a href="https://lab.documatch.eu/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3 bg-white text-blue-700 hover:bg-blue-50 font-bold rounded-xl transition-colors shadow-lg min-h-[44px]" data-testid="cta-lab-documatch">
                {language === "fr" ? "Évaluer ma maturité documentaire"
                  : language === "es" ? "Evaluar mi madurez documental"
                  : "Benchmark my document maturity"}
                <span className="ml-1">→</span>
              </a>
            </div>
          </div>
        </section>
        
        <section id="analyzer" className="section-padding">
          <AIAnalyzer 
            isVisible={showAnalyzer} 
            onStart={() => setShowAnalyzer(true)}
          />
        </section>
        
        <section id="ged" className="section-padding section-alt">
          <GEDSolutions />
        </section>

        {/* Sectors CTA — before Integrations & Connectors */}
        <section className="py-12 px-4 bg-white" data-testid="sectors-cta">
          <div className="max-w-5xl mx-auto rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-8 sm:p-12 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 -mr-32 -mt-32 pointer-events-none" />
            <div className="relative z-10">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-xs font-semibold uppercase tracking-wider mb-4">
                {language === "fr" ? "Recherche par secteur" : language === "es" ? "Búsqueda por sector" : "Search by sector"}
              </span>
              <h2 className="font-playfair text-2xl sm:text-3xl font-bold mb-3">
                {language === "fr" ? "Trouvez la solution GED idéale par activité et par secteur"
                  : language === "es" ? "Encuentra la solución GED ideal por actividad y por sector"
                  : "Find the ideal DMS by industry and business activity"}
              </h2>
              <p className="text-white/80 text-sm sm:text-base max-w-2xl mx-auto mb-6">
                {language === "fr" ? "Explorez notre portail sectoriel dédié : industrie, santé, finance, secteur public, juridique, RH, retail, éducation…"
                  : language === "es" ? "Explora nuestro portal sectorial dedicado: industria, salud, finanzas, sector público, jurídico, RRHH, retail, educación…"
                  : "Explore our dedicated sector portal: industry, healthcare, finance, public sector, legal, HR, retail, education…"}
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a href="https://sectores.documatch.eu/" target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-semibold text-sm transition-colors shadow-lg shadow-blue-900/40" data-testid="cta-sectors-discover">
                  {language === "fr" ? "Découvrir les solutions par secteur" : language === "es" ? "Descubrir soluciones por sector" : "Discover solutions by sector"}
                </a>
                <a href="https://sectores.documatch.eu/" target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-lg font-semibold text-sm border border-white/20 transition-colors" data-testid="cta-sectors-compare">
                  {language === "fr" ? "Comparer par activité" : language === "es" ? "Comparar por actividad" : "Compare by activity"}
                </a>
                <a href="https://sectores.documatch.eu/" target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-lg font-semibold text-sm border border-white/20 transition-colors" data-testid="cta-sectors-find">
                  {language === "fr" ? "Trouver la solution idéale" : language === "es" ? "Encontrar la solución ideal" : "Find the ideal solution"}
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="integrations" className="section-padding bg-slate-800">
          <Integrations />
        </section>
        
        <section id="news" className="section-padding section-alt">
          <News />
        </section>

        <section id="lead-magnet" className="section-padding bg-slate-50">
          <LeadMagnet />
        </section>
        
        <section id="about" className="section-padding">
          <About />
        </section>
        
        <section id="contact" className="section-padding bg-slate-800">
          <Contact onShowFAQ={() => setShowFAQ(true)} />
        </section>

        {/* CTA Final */}
        <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-indigo-700" data-testid="cta-final-section">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-white mb-8"
            >
              {t.ctaFinal.text}
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <button
                onClick={() => {
                  setShowAnalyzer(true);
                  setTimeout(() => scrollToSection("analyzer"), 100);
                }}
                className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl"
                data-testid="cta-final-button"
              >
                {t.ctaFinal.cta}
              </button>
            </motion.div>
          </div>
        </section>
      </main>
      
      <Footer onLegalClick={setLegalModal} />
      
      {!cookieAccepted && (
        <CookieBanner 
          onAccept={handleCookieAccept}
          onReject={handleCookieReject}
        />
      )}
      
      <AnimatePresence>
        {showFAQ && (
          <FAQModal onClose={() => setShowFAQ(false)} />
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {legalModal && (
          <LegalModals 
            type={legalModal} 
            onClose={() => setLegalModal(null)} 
          />
        )}
      </AnimatePresence>

      <ReturnVisitorPopup onStartAnalysis={() => {
        setShowAnalyzer(true);
        setTimeout(() => scrollToSection("analyzer"), 100);
      }} />

      {/* Sticky CTA */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 sm:bottom-6" data-testid="sticky-cta">
        <button
          onClick={() => {
            setShowAnalyzer(true);
            setTimeout(() => scrollToSection("analyzer"), 100);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-full shadow-xl text-sm flex items-center gap-2 transition-all hover:scale-105"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          {t.hero.cta}
        </button>
      </div>
    </>
    );
  };

  // GA page tracking component
  const GATracker = () => {
    const location = useLocation();
    useEffect(() => {
      trackPageView(location.pathname);
    }, [location]);
    return null;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
        scriptProps={{ async: true, defer: true, appendTo: "head" }}
      >
        <BrowserRouter>
          <GATracker />
          <div className="App min-h-screen" data-testid="app-container">
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/solution/:solutionId" element={<SolutionLanding />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/ebook" element={<EBook />} />
              <Route path="/sponsors" element={<SponsorsPage />} />
              {/* 410 GONE — GDN Solutions & SCO Consulting permanently removed */}
              <Route path="/solutions/gdn-solutions" element={<Gone resource="GDN Solutions" />} />
              <Route path="/solution/sco-consulting" element={<Gone resource="SCO Consulting" />} />
              {/* 301 REDIRECT — old article slug → new canonical slug */}
              <Route path="/blog/yooz-vs-zeendoc-2026" element={<Navigate to="/blog/pourquoi-comparatifs-ged-biaises" replace />} />
              <Route path="/blog/:slug" element={<BlogArticle />} />
              <Route path="/obligations-2026" element={<ObligationsLanding />} />
              <Route path="/obligations-documentaires-2026" element={<ObligationsLanding />} />
              <Route path="/obligaciones-2026" element={<ObligationsLanding />} />
            </Routes>
          </div>
        </BrowserRouter>
      </GoogleReCaptchaProvider>
    </LanguageContext.Provider>
  );
}

export default App;
